import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { functions } from "../firebase";

type AdminUserInfo = {
  uid: string;
  email: string;
  disabled: boolean;
  claims: Record<string, any>;
  profile: any | null;
};

type ParentInviteStatus = "not_invited" | "invited" | "activated" | "missing_email" | "inactive";

type ParentInviteQueueRow = {
  parentKey: string;
  firstName: string;
  lastName: string;
  email: string;
  familyId?: string | null;
  isActive: boolean;
  authUid?: string | null;
  inviteStatus: ParentInviteStatus;
  invitedAt?: string | null;
  activatedAt?: string | null;
};

type ParentInviteStats = {
  total: number;
  notInvited: number;
  invited: number;
  activated: number;
  missingEmail: number;
  inactive: number;
};

type AccessRole = {
  id: string;
  name: string;
  description?: string;
  protected?: boolean;
  system?: boolean;
  permissions?: Record<string, string>;
  assignableByActor?: boolean;
};

function normalizeEmail(v: string) {
  return String(v || "").trim().toLowerCase();
}

function truthy(v: any) {
  return v === true;
}

function computeEffectiveAccess(info: AdminUserInfo | null) {
  const claims = info?.claims ?? {};
  const profile = info?.profile ?? {};
  const role = String(claims.role ?? profile.role ?? "parent");

  const isSuperAdmin = truthy(claims.superAdmin) || role === "superAdmin";
  const isAdmin = isSuperAdmin || truthy(claims.admin) || role === "admin";
  const isOffice = isAdmin || truthy(claims.office) || role === "office";
  const isTeacher = isAdmin || truthy(claims.teacher) || role === "teacher";
  const isAppDeveloper =
    isSuperAdmin ||
    truthy(claims.appDeveloper) ||
    truthy(claims.developer) ||
    role === "developer" ||
    role === "appDeveloper";
  const isWebDeveloper = isSuperAdmin || truthy(claims.webDeveloper) || role === "webDeveloper";
  const isDeveloper = isAppDeveloper || isWebDeveloper;
  const isDeveloperAdmin = isSuperAdmin || truthy(claims.developerAdmin);
  const isStaff = isTeacher || isOffice || isAdmin || isSuperAdmin;

  return {
    role,
    isSuperAdmin,
    isAdmin,
    isOffice,
    isTeacher,
    isDeveloper,
    isAppDeveloper,
    isWebDeveloper,
    isDeveloperAdmin,
    isStaff,
  };
}

function displayNameForUser(u: AdminUserInfo) {
  const p = u.profile ?? {};
  const first = String(p.firstName ?? "").trim();
  const last = String(p.lastName ?? "").trim();
  const display = String(p.displayName ?? "").trim();

  if (first || last) return `${first} ${last}`.trim();
  if (display) return display;
  return u.email;
}

function effectiveAccessLabel(u: AdminUserInfo) {
  const e = computeEffectiveAccess(u);
  if (e.isSuperAdmin) return "Super Admin";
  if (e.isAdmin) return "Admin";
  if (e.isOffice) return "Office";
  if (e.isAppDeveloper) return "App Developer";
  if (e.isWebDeveloper) return "Web Developer";
  if (e.isTeacher) return "Teacher/Staff";
  return "Parent/Basic";
}

function inferredAccessRoleIds(info: AdminUserInfo | null) {
  if (!info) return [];
  const claims = info.claims ?? {};
  const profile = info.profile ?? {};
  const claimIds = Array.isArray(claims.accessRoleIds) ? claims.accessRoleIds.map(String).filter(Boolean) : [];
  const profileIds = Array.isArray(profile.accessRoleIds) ? profile.accessRoleIds.map(String).filter(Boolean) : [];
  if (claimIds.length) return claimIds;
  if (profileIds.length) return profileIds;

  const role = String(claims.role ?? profile.role ?? "parent");
  const ids = new Set<string>();
  if (claims.superAdmin === true || role === "superAdmin") ids.add("superAdmin");
  else if (claims.admin === true || role === "admin") ids.add("admin");
  else if (claims.office === true || role === "office") ids.add("office");
  else if (claims.teacher === true || role === "teacher") ids.add("teacher");
  else ids.add(["parent", "board", "ptu"].includes(role) ? role : "parent");
  if (claims.appDeveloper === true || claims.developer === true || role === "appDeveloper" || role === "developer") ids.add("appDeveloper");
  if (claims.webDeveloper === true || role === "webDeveloper") ids.add("webDeveloper");
  if (claims.board === true || role === "board") ids.add("board");
  if (claims.ptu === true || role === "ptu") ids.add("ptu");
  return Array.from(ids);
}


function parentInviteStatusLabel(status: ParentInviteStatus) {
  switch (status) {
    case "not_invited":
      return "Not invited";
    case "invited":
      return "Invited";
    case "activated":
      return "Activated";
    case "missing_email":
      return "Missing email";
    case "inactive":
      return "Inactive";
    default:
      return status;
  }
}

function parentInvitePillClass(status: ParentInviteStatus) {
  if (status === "activated") return "pill ok";
  if (status === "invited") return "pill";
  if (status === "not_invited") return "pill off";
  return "pill off";
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export function UsersPage() {
  const { user, isSuperAdmin } = useAuth();
  const accountPanelRef = useRef<HTMLDivElement | null>(null);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"parent" | "teacher" | "office" | "admin">("parent");

  const [loadingInvite, setLoadingInvite] = useState(false);
  const [loadingResetLink, setLoadingResetLink] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [savingAccessRoles, setSavingAccessRoles] = useState(false);

  const [info, setInfo] = useState<AdminUserInfo | null>(null);
  const [lastGeneratedLink, setLastGeneratedLink] = useState<string | null>(null);
  const [accessRoles, setAccessRoles] = useState<AccessRole[]>([]);
  const [selectedAccessRoleIds, setSelectedAccessRoleIds] = useState<string[]>([]);

  const [users, setUsers] = useState<AdminUserInfo[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [showDisabled, setShowDisabled] = useState(false);

  const [parentQueue, setParentQueue] = useState<ParentInviteQueueRow[]>([]);
  const [parentQueueStats, setParentQueueStats] = useState<ParentInviteStats>({
    total: 0,
    notInvited: 0,
    invited: 0,
    activated: 0,
    missingEmail: 0,
    inactive: 0,
  });
  const [parentQueueLoading, setParentQueueLoading] = useState(false);
  const [parentQueueError, setParentQueueError] = useState<string | null>(null);
  const [parentQueueSearch, setParentQueueSearch] = useState("");
  const [parentQueueFilter, setParentQueueFilter] = useState<"" | ParentInviteStatus>("");
  const [selectedParentKeys, setSelectedParentKeys] = useState<string[]>([]);
  const [batchInviteBusy, setBatchInviteBusy] = useState(false);

  const actorEmail = normalizeEmail(user?.email ?? "");
  const targetEmail = normalizeEmail(info?.email ?? "");
  const isSelf = !!info && !!actorEmail && actorEmail === targetEmail;

  const canInvite = useMemo(
    () => normalizeEmail(inviteEmail).includes("@") && !loadingInvite,
    [inviteEmail, loadingInvite]
  );

  async function loadUsers(searchOverride?: string) {
    setUsersLoading(true);
    setUsersError(null);

    try {
      const fn = httpsCallable(functions, "listAdminUsers");
      const res: any = await fn({
        includeDisabled: showDisabled,
        search: String(searchOverride ?? userSearch ?? "").trim(),
        maxResults: 200,
      });

      const list = Array.isArray(res?.data?.users) ? res.data.users : [];
      setUsers(list);
    } catch (e: any) {
      setUsers([]);
      setUsersError(e?.message || "Failed to load users.");
    } finally {
      setUsersLoading(false);
    }
  }


  async function loadParentQueue(searchOverride?: string, filterOverride?: "" | ParentInviteStatus) {
    setParentQueueLoading(true);
    setParentQueueError(null);
    try {
      const fn = httpsCallable(functions, "listParentInviteQueue");
      const res: any = await fn({
        search: String(searchOverride ?? parentQueueSearch ?? "").trim(),
        statusFilter: String(filterOverride ?? parentQueueFilter ?? "").trim(),
      });
      setParentQueue(Array.isArray(res?.data?.rows) ? res.data.rows : []);
      setParentQueueStats(
        res?.data?.stats ?? { total: 0, notInvited: 0, invited: 0, activated: 0, missingEmail: 0, inactive: 0 }
      );
      setSelectedParentKeys([]);
    } catch (e: any) {
      setParentQueue([]);
      setParentQueueError(e?.message ?? "Failed to load parent invite queue.");
    } finally {
      setParentQueueLoading(false);
    }
  }

  useEffect(() => {
    void loadParentQueue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentQueueFilter]);

  useEffect(() => {
    void loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDisabled]);

  async function loadAccessConfig() {
    try {
      const fn = httpsCallable(functions, "listAccessControlConfig");
      const res: any = await fn({});
      setAccessRoles(Array.isArray(res?.data?.roles) ? res.data.roles : []);
    } catch (e) {
      console.warn("[UsersPage] Failed to load access roles.", e);
      setAccessRoles([]);
    }
  }

  useEffect(() => {
    void loadAccessConfig();
  }, []);

  async function selectUser(selected: AdminUserInfo) {
    setInfo(selected);
    setSelectedAccessRoleIds(inferredAccessRoleIds(selected));
    setLastGeneratedLink(null);
    window.setTimeout(() => {
      accountPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);

    try {
      const fn = httpsCallable(functions, "getUserAdminInfo");
      const res: any = await fn({ targetUid: selected.uid });
      const u = res?.data?.user;
      if (!u?.uid) return;

      const nextInfo = {
        uid: u.uid,
        email: u.email ?? selected.email,
        disabled: u.disabled === true,
        claims: u.claims ?? {},
        profile: u.profile ?? null,
      };
      setInfo(nextInfo);
      setSelectedAccessRoleIds(inferredAccessRoleIds(nextInfo));
    } catch {
      // keep lightweight row data if detail refresh fails
    }
  }

  async function refreshSelectedAndList(targetEmailToRefresh?: string) {
    await loadUsers();
    if (targetEmailToRefresh) {
      const match = users.find((u) => normalizeEmail(u.email) === normalizeEmail(targetEmailToRefresh));
      if (match) {
        await selectUser(match);
      }
    }
  }

  async function createInvite() {
    const email = normalizeEmail(inviteEmail);
    if (!email) return;

    setLoadingInvite(true);
    setLastGeneratedLink(null);

    try {
      let fnName = "createParentInvite";
      let payload: any = { email, sendEmailNow: true };

      if (inviteRole === "parent") {
        fnName = "createParentInvite";
      } else if (inviteRole === "teacher") {
        fnName = "createTeacherInvite";
      } else {
        fnName = "createStaffInvite";
        payload = { email, role: inviteRole, sendEmailNow: true };
      }

      const fn = httpsCallable(functions, fnName);
      const res: any = await fn(payload);
      const link = String(res?.data?.passwordResetLink || "");
      const emailSent = res?.data?.emailSent === true;
      const emailError = String(res?.data?.emailError || "");

      alert(
        emailSent
          ? `Invite prepared and emailed to ${email}.`
          : emailError
            ? `Invite prepared for ${email}, but the email was not sent: ${emailError}`
            : `Invite prepared for ${email}, but the email was not sent.`
      );
      setInviteEmail("");
      setLastGeneratedLink(link || null);
      await loadUsers(email);
      await loadParentQueue(email, parentQueueFilter);

      const newMatch = (Array.isArray(users) ? users : []).find(
        (u) => normalizeEmail(u.email) === email
      );
      if (newMatch) {
        await selectUser(newMatch);
      }
    } catch (e: any) {
      alert(e?.message ?? "Failed to create invite.");
    } finally {
      setLoadingInvite(false);
    }
  }

  async function generatePasswordSetupLink() {
    if (!info?.email) return;

    setLoadingResetLink(true);
    setLastGeneratedLink(null);

    try {
      const fn = httpsCallable(functions, "createPasswordSetupLink");
      const res: any = await fn({ email: info.email, sendEmailNow: true });
      const link = String(res?.data?.passwordResetLink || "");
      const emailSent = res?.data?.emailSent === true;
      const emailError = String(res?.data?.emailError || "");

      if (!link) throw new Error("No password setup link returned.");

      setLastGeneratedLink(link);
      if (emailSent) {
        alert("Fresh account activation link generated and emailed.");
      } else {
        alert(
          emailError
            ? `Fresh account activation link generated, but the email was not sent: ${emailError}`
            : "Fresh account activation link generated, but the email was not sent."
        );
      }
    } catch (e: any) {
      alert(e?.message ?? "Failed to create password setup link.");
    } finally {
      setLoadingResetLink(false);
    }
  }

  async function copyLastLink() {
    if (!lastGeneratedLink) return;
    try {
      await navigator.clipboard.writeText(lastGeneratedLink);
      alert("Link copied to clipboard.");
    } catch {
      prompt("Copy this link:", lastGeneratedLink);
    }
  }

  function toggleAccessRole(roleId: string, checked: boolean) {
    setSelectedAccessRoleIds((prev) => {
      if (checked) return Array.from(new Set([...prev, roleId]));
      const next = prev.filter((id) => id !== roleId);
      return next.length ? next : prev;
    });
  }

  async function saveAccessRoles() {
    if (!info) return;
    if (!selectedAccessRoleIds.length) {
      alert("Select at least one access role.");
      return;
    }
    const proceed = confirm(
      `Apply selected access roles to ${info.email}?\n\n` +
      "This updates Firebase custom claims and the user must sign out and back in for the token to refresh."
    );
    if (!proceed) return;

    setSavingAccessRoles(true);
    try {
      const fn = httpsCallable(functions, "assignUserAccessRoles");
      const res: any = await fn({ targetUid: info.uid, roleIds: selectedAccessRoleIds });
      alert("Access roles updated. User must sign out/in to refresh token.");
      await loadUsers(userSearch);
      await selectUser({ ...info, claims: res?.data?.claims ?? info.claims });
    } catch (e: any) {
      alert(e?.message ?? "Failed to update access roles.");
    } finally {
      setSavingAccessRoles(false);
    }
  }

  async function lock(reasonCode: string) {
    if (!info) return;
    const detail = prompt("Reason / remediation notes (visible to admins):", "");
    try {
      const fn = httpsCallable(functions, "lockUserAccount");
      await fn({ targetUid: info.uid, reasonCode, detail: detail ?? "" });
      alert("Account locked (Auth disabled).");
      await loadUsers(userSearch);
      await selectUser(info);
    } catch (e: any) {
      alert(e?.message ?? "Failed to lock account.");
    }
  }

  async function unlock() {
    if (!info) return;
    const detail = prompt("Unlock notes / remediation completed:", "");
    try {
      const fn = httpsCallable(functions, "unlockUserAccount");
      await fn({ targetUid: info.uid, detail: detail ?? "" });
      alert("Account unlocked.");
      await loadUsers(userSearch);
      await selectUser(info);
    } catch (e: any) {
      alert(e?.message ?? "Failed to unlock account.");
    }
  }

  async function deleteUser() {
    if (!info) return;

    const ok = confirm(
      `Delete/deprovision this user?\n\n${info.email}\n\nThis will:\n- delete Firebase Auth user\n- remove userProfile\n- remove inbox/device tokens\n- unlink roster linkages\n- remove parent/student or teacher/student links\n\nThis action is destructive.`
    );
    if (!ok) return;

    setLoadingDelete(true);
    try {
      const fn = httpsCallable(functions, "deleteUserAdmin");
      await fn({ targetUid: info.uid });
      alert("User deleted/deprovisioned.");
      setInfo(null);
      setLastGeneratedLink(null);
      await loadUsers(userSearch);
      await loadParentQueue(parentQueueSearch, parentQueueFilter);
    } catch (e: any) {
      alert(e?.message ?? "Failed to delete user.");
    } finally {
      setLoadingDelete(false);
    }
  }


  function toggleParentSelection(parentKey: string, checked: boolean) {
    setSelectedParentKeys((prev) => {
      if (checked) return Array.from(new Set([...prev, parentKey]));
      return prev.filter((k) => k !== parentKey);
    });
  }

  function selectedQueueRows() {
    return parentQueue.filter((r) => selectedParentKeys.includes(r.parentKey));
  }

  async function sendInviteToParent(row: ParentInviteQueueRow) {
    if (!row.email) {
      alert("This parent is missing an email address.");
      return;
    }
    setBatchInviteBusy(true);
    try {
      const fn = httpsCallable(functions, "createParentInvite");
      const res: any = await fn({ email: row.email, sendEmailNow: true });
      const emailSent = res?.data?.emailSent === true;
      const emailError = String(res?.data?.emailError || "");
      await loadParentQueue();
      await loadUsers(userSearch);
      alert(
        emailSent
          ? `Invite sent to ${row.email}.`
          : emailError
            ? `Invite prepared for ${row.email}, but the email was not sent: ${emailError}`
            : `Invite prepared for ${row.email}, but the email was not sent.`
      );
    } catch (e: any) {
      alert(e?.message ?? "Failed to send invite.");
    } finally {
      setBatchInviteBusy(false);
    }
  }

  async function sendInvitesToSelected() {
    const rows = selectedQueueRows().filter((r) => r.email && r.inviteStatus !== "activated" && r.inviteStatus !== "inactive");
    if (!rows.length) {
      alert("Select at least one invite-eligible parent.");
      return;
    }
    const ok = confirm(`Send invite emails to ${rows.length} selected parent account(s)?`);
    if (!ok) return;
    setBatchInviteBusy(true);
    try {
      const fn = httpsCallable(functions, "createParentInvite");
      let sent = 0;
      const failures: string[] = [];
      for (const row of rows) {
        const res: any = await fn({ email: row.email, sendEmailNow: true });
        const emailSent = res?.data?.emailSent === true;
        const emailError = String(res?.data?.emailError || "");
        if (emailSent) sent += 1;
        else failures.push(`${row.email}: ${emailError || "email was not sent"}`);
      }
      await loadParentQueue();
      await loadUsers(userSearch);
      alert(
        failures.length
          ? `Sent ${sent} of ${rows.length} invite email(s).\n\n${failures.slice(0, 5).join("\n")}${failures.length > 5 ? "\n..." : ""}`
          : `Sent ${rows.length} invite email(s).`
      );
    } catch (e: any) {
      alert(e?.message ?? "Failed while sending invites.");
    } finally {
      setBatchInviteBusy(false);
    }
  }

  async function sendInvitesToAllNotInvited() {
    const rows = parentQueue.filter((r) => r.inviteStatus === "not_invited" && !!r.email);
    if (!rows.length) {
      alert("There are no not-yet-invited parents with email addresses in the current filter.");
      return;
    }
    const ok = confirm(`Send invite emails to all ${rows.length} not-yet-invited parents in the current view?`);
    if (!ok) return;
    setBatchInviteBusy(true);
    try {
      const fn = httpsCallable(functions, "createParentInvite");
      let sent = 0;
      const failures: string[] = [];
      for (const row of rows) {
        const res: any = await fn({ email: row.email, sendEmailNow: true });
        const emailSent = res?.data?.emailSent === true;
        const emailError = String(res?.data?.emailError || "");
        if (emailSent) sent += 1;
        else failures.push(`${row.email}: ${emailError || "email was not sent"}`);
      }
      await loadParentQueue();
      await loadUsers(userSearch);
      alert(
        failures.length
          ? `Sent ${sent} of ${rows.length} invite email(s).\n\n${failures.slice(0, 5).join("\n")}${failures.length > 5 ? "\n..." : ""}`
          : `Sent ${rows.length} invite email(s).`
      );
    } catch (e: any) {
      alert(e?.message ?? "Failed while sending invites.");
    } finally {
      setBatchInviteBusy(false);
    }
  }

  const effective = computeEffectiveAccess(info);
  const targetIsSuperAdmin = effective.isSuperAdmin;
  const canEditTarget = !targetIsSuperAdmin || isSuperAdmin;
  const canDeleteTarget = canEditTarget && !isSelf;

  return (
    <div className="card">
      <h2>Users / Accounts</h2>
      <p className="muted">
        Create Firebase Auth accounts, send password setup links, inspect role/profile state,
        assign access roles, lock/unlock accounts, and deprovision users.
      </p>

      <hr className="hr" />

      <div className="card" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Create Invite</h3>

        <div className="grid2">
          <div>
            <div className="label">Email</div>
            <input
              className="input"
              placeholder="user@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
          </div>

          <div>
            <div className="label">Primary role</div>
            <select
              className="input"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as any)}
            >
              <option value="parent">parent</option>
              <option value="teacher">teacher</option>
              <option value="office">office</option>
              <option value="admin">admin</option>
            </select>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <button className="btn" disabled={!canInvite} onClick={createInvite}>
            {loadingInvite ? "Creating…" : "Create Invite + Email"}
          </button>
        </div>

        <p className="muted" style={{ marginTop: 10, fontSize: 12 }}>
          This creates the Auth account if needed, prepares a password setup link,
          applies the requested role, and sends the branded email.
          Super Admin is intentionally excluded from this invite flow.
        </p>
      </div>

      {lastGeneratedLink ? (
        <>
          <hr className="hr" />
          <div className="success">
            An account activation link was generated.
            <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="btn" onClick={copyLastLink}>Copy Link</button>
            </div>
            <div style={{ marginTop: 10, wordBreak: "break-all", fontSize: 12 }}>
              {lastGeneratedLink}
            </div>
          </div>
        </>
      ) : null}

      <hr className="hr" />

      <div className="card" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Parent Invite Queue</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Review parent records created from roster imports, then send invites in batches or individually.
        </p>

        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div className="card" style={{ padding: 12, minWidth: 120 }}><b>Not invited</b><div>{parentQueueStats.notInvited}</div></div>
            <div className="card" style={{ padding: 12, minWidth: 120 }}><b>Invited</b><div>{parentQueueStats.invited}</div></div>
            <div className="card" style={{ padding: 12, minWidth: 120 }}><b>Activated</b><div>{parentQueueStats.activated}</div></div>
            <div className="card" style={{ padding: 12, minWidth: 120 }}><b>Missing email</b><div>{parentQueueStats.missingEmail}</div></div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input
              className="input"
              placeholder="Search parent ID, name, email, family…"
              value={parentQueueSearch}
              onChange={(e) => setParentQueueSearch(e.target.value)}
              style={{ minWidth: 320, flex: 1 }}
            />
            <select
              className="input"
              value={parentQueueFilter}
              onChange={(e) => setParentQueueFilter(e.target.value as "" | ParentInviteStatus)}
              style={{ minWidth: 180 }}
            >
              <option value="">All statuses</option>
              <option value="not_invited">Not invited</option>
              <option value="invited">Invited</option>
              <option value="activated">Activated</option>
              <option value="missing_email">Missing email</option>
              <option value="inactive">Inactive</option>
            </select>
            <button className="btn secondary" disabled={parentQueueLoading} onClick={() => loadParentQueue()}>
              {parentQueueLoading ? "Loading…" : "Refresh"}
            </button>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="btn" disabled={batchInviteBusy} onClick={sendInvitesToSelected}>
              {batchInviteBusy ? "Sending…" : "Send Invites to Selected"}
            </button>
            <button className="btn secondary" disabled={batchInviteBusy} onClick={sendInvitesToAllNotInvited}>
              {batchInviteBusy ? "Sending…" : "Send Invites to All Not Invited"}
            </button>
          </div>
        </div>

        {parentQueueError ? <div style={{ marginTop: 10, color: "#b00020", fontWeight: 700 }}>{parentQueueError}</div> : null}

        <div style={{ marginTop: 12 }}>
          {parentQueueLoading ? (
            <div className="muted">Loading parent invite queue…</div>
          ) : parentQueue.length === 0 ? (
            <div className="muted">No parent records found for the current filter.</div>
          ) : (
            <div className="table">
              <div className="thead" style={{ gridTemplateColumns: "40px 1.2fr 170px 130px 170px 1fr" }}>
                <div></div>
                <div>Parent</div>
                <div>Status</div>
                <div>Parent ID</div>
                <div>Family</div>
                <div></div>
              </div>
              {parentQueue.map((r) => {
                const checked = selectedParentKeys.includes(r.parentKey);
                const canInviteRow = !!r.email && r.inviteStatus !== "activated" && r.inviteStatus !== "inactive";
                return (
                  <div className="trow" key={r.parentKey} style={{ gridTemplateColumns: "40px 1.2fr 170px 130px 170px 1fr" }}>
                    <div>
                      <input type="checkbox" checked={checked} onChange={(e) => toggleParentSelection(r.parentKey, e.target.checked)} />
                    </div>
                    <div>
                      <div><b>{[r.firstName, r.lastName].filter(Boolean).join(" ") || r.email || r.parentKey}</b></div>
                      <div className="muted" style={{ fontSize: 12 }}>{r.email || "No email on file"}</div>
                      {r.invitedAt ? <div className="muted" style={{ fontSize: 12 }}>Invited: {formatDateTime(r.invitedAt)}</div> : null}
                      {r.activatedAt ? <div className="muted" style={{ fontSize: 12 }}>Activated: {formatDateTime(r.activatedAt)}</div> : null}
                    </div>
                    <div><span className={parentInvitePillClass(r.inviteStatus)}>{parentInviteStatusLabel(r.inviteStatus)}</span></div>
                    <div>{r.parentKey}</div>
                    <div>{r.familyId || "—"}</div>
                    <div style={{ textAlign: "right" }}>
                      <button className="btn small secondary" disabled={!canInviteRow || batchInviteBusy} onClick={() => sendInviteToParent(r)}>
                        {r.inviteStatus === "invited" ? "Resend Invite" : "Send Invite"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <hr className="hr" />

      <div className="card" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Active Users</h3>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input
            className="input"
            placeholder="Search email, display name, parent ID, teacher key…"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            style={{ minWidth: 320, flex: 1 }}
          />
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
            />
            <span>Show disabled</span>
          </label>
          <button className="btn secondary" disabled={usersLoading} onClick={() => loadUsers()}>
            {usersLoading ? "Loading…" : "Refresh"}
          </button>
        </div>

        {usersError ? (
          <div style={{ marginTop: 10, color: "#b00020", fontWeight: 700 }}>
            {usersError}
          </div>
        ) : null}

        <div style={{ marginTop: 12 }}>
          {usersLoading ? (
            <div className="muted">Loading users…</div>
          ) : users.length === 0 ? (
            <div className="muted">No users found.</div>
          ) : (
            <div className="table">
              <div
                className="thead"
                style={{ gridTemplateColumns: "1.3fr 140px 110px 150px 1fr" }}
              >
                <div>User</div>
                <div>Access</div>
                <div>Status</div>
                <div>Roster Link</div>
                <div></div>
              </div>

              {users.map((u) => {
                const isSelected = info?.uid === u.uid;
                const rosterLink =
                  u.profile?.teacherKey
                    ? `Teacher: ${u.profile.teacherKey}`
                    : u.profile?.parentKey
                      ? `Parent: ${u.profile.parentKey}`
                      : "—";

                return (
                  <div
                    className="trow"
                    key={u.uid}
                    style={{
                      gridTemplateColumns: "1.3fr 140px 110px 150px 1fr",
                      background: isSelected ? "rgba(22,58,95,0.06)" : undefined,
                    }}
                  >
                    <div>
                      <div><b>{displayNameForUser(u)}</b></div>
                      <div className="muted" style={{ fontSize: 12 }}>{u.email}</div>
                    </div>
                    <div>{effectiveAccessLabel(u)}</div>
                    <div>
                      <span className={u.disabled ? "pill off" : "pill ok"}>
                        {u.disabled ? "Disabled" : "Active"}
                      </span>
                    </div>
                    <div>{rosterLink}</div>
                    <div style={{ textAlign: "right" }}>
                      <button className="btn small secondary" onClick={() => selectUser(u)}>
                        Manage
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {info ? (
        <>
          <hr className="hr" />

          <div className="card" style={{ padding: 16 }} ref={accountPanelRef}>
            <h3 style={{ marginTop: 0 }}>Account Summary</h3>

            <div style={{ display: "grid", gap: 6 }}>
              <div><b>Email:</b> {info.email}</div>
              <div><b>UID:</b> <code>{info.uid}</code></div>
              <div>
                <b>Status:</b>{" "}
                {info.disabled ? <span style={{ color: "tomato" }}>LOCKED / disabled</span> : "active"}
              </div>
              <div><b>Profile Role:</b> {String(info.profile?.role ?? "—")}</div>
              <div><b>Effective Access:</b> {effective.isSuperAdmin ? "Super Admin" : effective.isAdmin ? "Admin" : effective.isOffice ? "Office" : effective.isTeacher ? "Teacher/Staff" : "Parent/Basic"}</div>
              <div><b>Developer Access:</b> {effective.isAppDeveloper ? "App Developer" : effective.isWebDeveloper ? "Web Developer" : "none"}</div>
              <div><b>Parent ID:</b> {String(info.profile?.parentKey ?? "—")}</div>
              <div><b>Teacher Key:</b> {String(info.profile?.teacherKey ?? "—")}</div>
              <div><b>Family ID:</b> {String(info.profile?.familyId ?? "—")}</div>
            </div>

            {isSelf ? (
              <div className="muted" style={{ marginTop: 10 }}>
                You are viewing your own account. Self-deletion is blocked.
              </div>
            ) : null}

            {targetIsSuperAdmin ? (
              <div className="muted" style={{ marginTop: 10 }}>
                This target account is Super Admin protected.
              </div>
            ) : null}
          </div>

          <hr className="hr" />

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end" }}>
            <button className="btn secondary" disabled={loadingResetLink} onClick={generatePasswordSetupLink}>
              {loadingResetLink ? "Sending email…" : "Resend Account Setup Email"}
            </button>

            {info.disabled ? (
              <button className="btn" disabled={!canEditTarget} onClick={unlock}>Unlock</button>
            ) : (
              <>
                <button className="btn secondary" disabled={!canEditTarget} onClick={() => lock("suspected_compromise")}>
                  Lock: suspected compromise
                </button>
                <button className="btn secondary" disabled={!canEditTarget} onClick={() => lock("policy_violation")}>
                  Lock: policy violation
                </button>
                <button className="btn secondary" disabled={!canEditTarget} onClick={() => lock("manual")}>
                  Lock: manual
                </button>
              </>
            )}

            <button
              className="btn"
              disabled={loadingDelete || !canDeleteTarget}
              onClick={deleteUser}
              style={{ background: "#b00020" }}
            >
              {loadingDelete ? "Deleting…" : "Delete / Deprovision User"}
            </button>
          </div>

          <hr className="hr" />

          <div className="card" style={{ padding: 16 }}>
            <div style={{ display: "flex", gap: 12, justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap" }}>
              <div>
                <h3 style={{ marginTop: 0 }}>Access Roles</h3>
                <p className="muted" style={{ marginTop: 0 }}>
                  Preferred access model. These roles compile into Firebase claims and preserve the legacy flags used by the app and security rules.
                </p>
              </div>
              <button className="btn secondary" type="button" onClick={() => void loadAccessConfig()}>
                Refresh Roles
              </button>
            </div>

            {accessRoles.length ? (
              <div className="grid2">
                {accessRoles.map((role) => {
                  const checked = selectedAccessRoleIds.includes(role.id);
                  const locked = role.assignableByActor === false || (!isSuperAdmin && role.protected === true);
                  return (
                    <label key={role.id} className="notice-light" style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => toggleAccessRole(role.id, e.target.checked)}
                        disabled={!canEditTarget || savingAccessRoles || locked}
                        style={{ marginTop: 3 }}
                      />
                      <span>
                        <b>{role.name}</b>
                        {role.system ? <span className="pill" style={{ marginLeft: 8 }}>Built-in</span> : null}
                        {role.protected ? <span className="pill off" style={{ marginLeft: 8 }}>Protected</span> : null}
                        {locked && !isSuperAdmin ? <span className="pill off" style={{ marginLeft: 8 }}>Super Admin</span> : null}
                        <span className="muted" style={{ display: "block", fontSize: 12 }}>{role.description || role.id}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="muted">No access roles loaded yet.</p>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 12 }}>
              <button className="btn secondary" type="button" onClick={() => setSelectedAccessRoleIds(inferredAccessRoleIds(info))} disabled={savingAccessRoles}>
                Reset Selection
              </button>
              <button className="btn" type="button" onClick={() => void saveAccessRoles()} disabled={!canEditTarget || savingAccessRoles || !selectedAccessRoleIds.length}>
                {savingAccessRoles ? "Saving..." : "Save Access Roles"}
              </button>
            </div>

            <p className="muted" style={{ marginTop: 10, fontSize: 12 }}>
              Admins can assign normal roles. Protected roles and super-admin-only modules require Super Admin.
            </p>
          </div>

          <hr className="hr" />

          <details>
            <summary>Raw claims / profile</summary>
            <pre style={{ whiteSpace: "pre-wrap" }}>
{JSON.stringify({ claims: info.claims, profile: info.profile }, null, 2)}
            </pre>
          </details>
        </>
      ) : null}
    </div>
  );
}
